import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Print } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import {
  CONTRACT_TERMS_ENDPOINT,
  CONTRACT_TERM_KIND_OPTIONS,
  SETTLEMENT_CYCLE_OPTIONS,
  SETTLEMENT_KIND_OPTIONS,
} from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { formatNumber } from '../../../../utils';
import { getContractTermEquipmentLabel } from '../../../../utils/settlementContractTerms';
import EquipmentReferenceList from '../../../../components/ui/entityReference/equipmentReferenceList.component';
import { ContractTermListPrice } from '../../../../types/contracts';
import {
  equalsNumberFilterOperators,
  exactDateFilterOperators,
  singleSelectFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const ContractTerms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewContractTerms = useHasPermission("settlement.view_contractterm");
  const canViewEquipment = useHasPermission("settlement.view_equipment");

  const KIND_OPTIONS = useMemo(
    () => CONTRACT_TERM_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const SETTLEMENT_CYCLE_SELECT_OPTIONS = useMemo(
    () => SETTLEMENT_CYCLE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const SETTLEMENT_KIND_SELECT_OPTIONS = useMemo(
    () => SETTLEMENT_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "equipments",
      headerName: t("Equipment"),
      filterable: false,
      valueGetter: (_value, row) => getContractTermEquipmentLabel(row),
      renderCell: (params) => (
        <EquipmentReferenceList
          equipments={params.row.equipments || []}
          canPreview={canViewEquipment}
        />
      ),
    },
    {
      field: "kind",
      headerName: t("Kind"),
      type: "singleSelect",
      valueOptions: KIND_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.kind,
      renderCell: (params) => params.row.kind_display,
    },
    {
      field: "active",
      headerName: t("Active"),
      type: "boolean",
      valueGetter: (_value, row) => row.active !== false,
      renderCell: (params) => {
        const isActive = params.row.active !== false;
        return (
          <Chip
            label={isActive ? t("Yes") : t("No")}
            size="small"
            color={isActive ? "success" : "default"}
            variant={isActive ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      field: "settlement_cycle",
      headerName: t("Settlement Cycle"),
      type: "singleSelect",
      valueOptions: SETTLEMENT_CYCLE_SELECT_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.settlement_cycle,
      renderCell: (params) => params.row.settlement_cycle_display,
    },
    {
      field: "settlement_kind",
      headerName: t("Settlement kind"),
      type: "singleSelect",
      valueOptions: SETTLEMENT_KIND_SELECT_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.settlement_kind,
      renderCell: (params) => params.row.settlement_kind_display || "-",
    },
    {
      // Backend only declares an exact-match filter for this field (no
      // _gte/_lte pair), so only the `is` operator maps to the bare
      // next_settlement_date=value param crud.ts sends for it.
      field: "next_settlement_date",
      headerName: t("Next Settlement Date"),
      filterOperators: exactDateFilterOperators(),
    },
    {
      field: "rent_price",
      headerName: t("Rent Price"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatNumber(value),
    },
    {
      // Only a sale term carries one, so the column reads "-" on every rent
      // term rather than being hidden per row.
      field: "equipment_price",
      headerName: t("Equipment price"),
      type: "number",
      filterable: false,
      valueFormatter: (value) =>
        value == null || value === "" ? "-" : formatNumber(value),
    },
    {
      field: "warranty_end_date",
      headerName: t("Warranty end date"),
      filterable: false,
      valueFormatter: (value) => value || "-",
    },
    {
      field: "prices_info",
      headerName: t("Prices"),
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) => {
        const prices: ContractTermListPrice[] = row.prices_info || [];
        return prices.map((p) => `${p.kind_display || "-"}: ${p.price} zł`).join(", ");
      },
      renderCell: (params) => {
        const prices: ContractTermListPrice[] = params.row.prices_info || [];
        if (prices.length === 0) {
          return <Typography variant="body2">-</Typography>;
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
            {prices.map((price, index) => (
              <Typography key={index} variant="body2">
                {price.kind_display || "-"}: {formatNumber(price.price)} zł
              </Typography>
            ))}
          </Box>
        );
      },
    },
  ], [t, canViewEquipment, KIND_OPTIONS, SETTLEMENT_CYCLE_SELECT_OPTIONS, SETTLEMENT_KIND_SELECT_OPTIONS]);

  const handleRowClick = (contractTermId: string) => {
    navigate(`/contracts/${id}/equipment/${contractTermId}`);
  };

  if (!canViewContractTerms) {
    return null;
  }

  const contractTermsUrl = id ? `${CONTRACT_TERMS_ENDPOINT}?contract=${id}` : CONTRACT_TERMS_ENDPOINT;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Print />}
        title={t('Equipments')}
        url={contractTermsUrl}
        gridSettingsKey="CONTRACT_TERMS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="contractTermUpdated"
      />
    </Box>
  );
};
